import { useCallback, useEffect, useState } from 'react';

export interface AsyncOptions<T> {
  items: T[];
  status: 'idle' | 'loading' | 'success' | 'error';
  retry: () => void;
  error?: string;
}
const emptyItems: never[] = [];

/** A changed loader/context cannot expose options from the previous request. */
export function useAsyncOptions<T>(
  load: (isCurrent: () => boolean) => Promise<T[]>,
  enabled = true,
): AsyncOptions<T> {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    load: typeof load;
    attempt: number;
    items: T[];
    status: AsyncOptions<T>['status'];
    error?: string;
  }>({ load, attempt, items: emptyItems, status: 'idle' });
  const retry = useCallback(() => setAttempt(previous => previous + 1), []);
  useEffect(() => {
    let cancelled = false;
    const pending = { load, attempt, items: emptyItems };
    setResult({ ...pending, status: enabled ? 'loading' : 'idle' });
    if (enabled) {
      Promise.resolve()
        .then(() => (cancelled ? emptyItems : load(() => !cancelled)))
        .then(
          items => {
            if (!cancelled) setResult({ ...pending, items, status: 'success' });
          },
          (cause: unknown) => {
            if (!cancelled)
              setResult({
                ...pending,
                status: 'error',
                error: cause instanceof Error ? cause.message : String(cause),
              });
          },
        );
    }
    return () => {
      cancelled = true;
    };
  }, [load, enabled, attempt]);
  if (!enabled) return { items: emptyItems, status: 'idle', retry };
  if (result.load !== load || result.attempt !== attempt) {
    return { items: emptyItems, status: 'loading', retry };
  }
  return {
    items: result.items,
    status: result.status,
    error: result.error,
    retry,
  };
}
